import { useState, useRef } from 'react';
import { ProcessingIcon, CheckIcon, CopyIcon } from '../../../icons';
import { newToastErrorMessage, newToastSuccessMessage } from '@zappar/zpr-ui-components-stencil/utils';

export interface InlineInputProps {
  /** Input value */
  value: string;
  /** Input name attribute */
  name?: string;
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Accessible label */
  ariaLabel?: string;
  /** Make input read-only */
  readOnly?: boolean;
  /** Disable input interactions */
  disabled?: boolean;
  /** Mark input as required */
  required?: boolean;
  /** Show loading spinner */
  isLoading?: boolean;
  /** Show success state icon */
  isSuccess?: boolean;
  /** Show error state icon */
  isError?: boolean;
  /** Optional error message to display */
  errorMessage?: string;
  /** Value change callback */
  onChange?: (value: string) => void;
  /** Edit callback (e.g., on Enter key press) */
  onEdit?: () => void | Promise<void>;
  /** Show a copy button that copies the field value to the clipboard */
  copyable?: boolean;
  /** Render a textarea instead of an input */
  isTextarea?: boolean;
  /** Optional wrapper class */
  className?: string;
}

export const InlineInput = ({
  value,
  name = 'inline-input',
  label,
  placeholder = 'Enter value',
  ariaLabel = 'Inline input',
  readOnly = false,
  disabled = false,
  required = true,
  isLoading = false,
  isSuccess = false,
  isError = false,
  errorMessage = '',
  onChange,
  onEdit,
  copyable = false,
  isTextarea = false,
  className = '',
}: InlineInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async () => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }

    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }

    if (typeof navigator.clipboard !== 'undefined' && typeof navigator.clipboard.writeText !== 'undefined') {
      try {
        await navigator.clipboard.writeText(value);
        setIsCopied(true);
        newToastSuccessMessage(`${ariaLabel} has been copied to the clipboard`)
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        newToastErrorMessage(`Unable to copy ${ariaLabel} to the clipboard. Please check your browser permissions.`)
      }
    } else {
      document.execCommand('copy');
      setIsCopied(true);
      newToastSuccessMessage(`${ariaLabel} has been copied to the clipboard`)
      setTimeout(() => setIsCopied(false), 2000);
    }

    if (inputRef.current) {
      inputRef.current.value = value;
    }
    if (textareaRef.current) {
      textareaRef.current.value = value;
    }
  };

  return (
    <div className={`field ${className} ${copyable ? 'copyable' : ''}`}>
      { label && (
        <div className='label-wrapper'>
          <label htmlFor={name}>{label}</label>
        </div>
      )}
      <div className={`input-wrapper ${isLoading || isSuccess || copyable ? 'icon-right' : ''}`}>
        {!isTextarea ? (
          <input
            ref={inputRef}
            type="text"
            name={name}
            readOnly={readOnly}
            disabled={disabled}
            required={required}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onEdit?.();
              }
            }}
            placeholder={placeholder}
            aria-label={ariaLabel}
            className={`${isError ? 'invalid' : ''}`}
          />
        ) : (
          <textarea
            ref={textareaRef}
            name={name}
            readOnly={readOnly}
            disabled={disabled}
            required={required}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onEdit}
            placeholder={placeholder}
            aria-label={ariaLabel}
            className={`${isError ? 'invalid' : ''}`}
          ></textarea>
        )}
        {isLoading && (
            <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" style={{ right: '16px' }} className='spinner' />
        )}
        {isSuccess && !isLoading && (
          <CheckIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" style={{ right: '16px' }} className="success-icon"  />
        )}
        {copyable && !isLoading && !isSuccess && (
          <button
            type="button"
            className="btn btn-ghost copy-btn"
            aria-label="Copy to clipboard"
            title={isCopied ? 'Copied!' : 'Copy to clipboard'}
            onClick={copyToClipboard}
          >
            <CopyIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
          </button>
        )}
      </div>
      {isError && errorMessage && <p className="error-message">{errorMessage}</p>}
    </div>
  );
};
